import { ChevronLeft, ChevronRight, CloseX } from '@app/components/icons/ui-icons';
import { DiaryEntryDto } from '@app/models/dtos/contentDto';
import { formatDiaryDate } from '@app/utils/stringUtils';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Image from 'next/image';
import { useCallback, useEffect, useRef } from 'react';

interface PhotoViewerProps {
    entries: DiaryEntryDto[];
    index: number | null;
    onClose: () => void;
    onMove: (next: number) => void;
}

/** Full-screen look at one photo. Esc closes, arrows move, backdrop closes. */
export default function PhotoViewer({ entries, index, onClose, onMove }: PhotoViewerProps) {
    const reduced = useReducedMotion();
    const closeRef = useRef<HTMLButtonElement>(null);
    const open = index !== null;
    const entry = open ? entries[index] : undefined;

    const move = useCallback(
        (step: number) => {
            if (index === null) return;
            onMove((index + step + entries.length) % entries.length);
        },
        [index, entries.length, onMove]
    );

    useEffect(() => {
        if (!open) return;
        closeRef.current?.focus();

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
            if (event.key === 'ArrowRight') move(1);
            if (event.key === 'ArrowLeft') move(-1);
        };
        window.addEventListener('keydown', onKey);

        // Stop the page behind from scrolling while the viewer is up.
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = previous;
        };
    }, [open, onClose, move]);

    return (
        <AnimatePresence>
            {open && entry && entry.type === 'photo' && (
                <motion.div
                    className="fixed inset-0 z-[100] flex flex-col bg-[rgba(8,8,9,0.97)]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: reduced ? 0.12 : 0.25 }}
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${entry.place}, ${formatDiaryDate(entry.date)}`}
                    onClick={onClose}
                >
                    <div className="flex items-start justify-between gap-6 px-5 py-4 md:px-8">
                        <div className="min-w-0">
                            <p className="t-script-sm leading-none text-accent">{formatDiaryDate(entry.date)}</p>
                            <p className="t-small mt-2 truncate text-muted">{entry.place}</p>
                        </div>
                        <button
                            ref={closeRef}
                            type="button"
                            onClick={onClose}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-hairline text-ink transition-colors hover:border-accent hover:text-accent"
                            aria-label="Close"
                        >
                            <CloseX size={18} />
                        </button>
                    </div>

                    <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-4 md:px-16">
                        <motion.div
                            key={entry.id}
                            className="relative h-full w-full"
                            initial={{ opacity: 0, scale: reduced ? 1 : 0.99 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: reduced ? 0.12 : 0.3 }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <Image src={entry.src} alt={entry.alt} fill sizes="100vw" className="object-contain" priority />
                        </motion.div>

                        {entries.length > 1 && (
                            <>
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        move(-1);
                                    }}
                                    className="bg-paper/60 absolute left-2 flex h-11 w-11 items-center justify-center rounded-full border border-hairline text-ink backdrop-blur transition-colors hover:border-accent hover:text-accent md:left-5"
                                    aria-label="Previous photo"
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        move(1);
                                    }}
                                    className="bg-paper/60 absolute right-2 flex h-11 w-11 items-center justify-center rounded-full border border-hairline text-ink backdrop-blur transition-colors hover:border-accent hover:text-accent md:right-5"
                                    aria-label="Next photo"
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </>
                        )}
                    </div>

                    {entry.caption && (
                        <p className="t-small px-5 pb-6 text-center text-muted md:px-8" onClick={(event) => event.stopPropagation()}>
                            {entry.caption}
                        </p>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
}
